import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { suppliersColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'
import { SupplierService } from '../../services/entity-services/supplier.services'

const DEFAULT_FILTERS = {
  name: '',
}

@Component({
  selector: 'app-suppliers',
  templateUrl: 'suppliers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SuppliersComponent extends BaseListing implements OnInit {
  private supplierService = inject(SupplierService)

  listingColumns = signal(suppliersColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const v = this.filters()
    const f: Record<string, any> = {}
    if (v.name?.length) f['name'] = v.name
    return f
  })

  readonly querySuppliers = injectInfiniteQuery(() => ({
    queryKey: ['suppliers', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      // Persist current view settings before fetching, per tags.component.ts pattern
      return await this.supplierService
        .getSuppliers(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          { id: null, name: null }
        )
        .then(result => {
          this.saveViewSettings('suppliers', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.querySuppliers.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.querySuppliers.data()
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('suppliers')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('suppliers')
    this.querySuppliers.refetch()
  }

  setFilterName(value: string) {
    this.filters.update(f => ({ ...f, name: value }))
  }

  async clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.querySuppliers.refetch()
  }
}
